import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { createPortal } from "react-dom";

import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

import api from "@/services/api";
import TopMenu from "./components/TopMenu";
import MultiSelect from "@/components/MultiSelect";
import ItemModal from "../components/itemModal";

export default ({ project }) => {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    search: "",
    statuses: ["TODO", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO"],
    urgent: [],
    module_ids: [],
    roadlog_tags: [],
    is_in_backlog: "",
  });
  const [sortBy, setSortBy] = useState({ key: "points", value: 1 });
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const itemModalId = query.get("item_modal_id");

  useEffect(() => {
    fetchItems();
  }, [filters]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/roadlog_item/search", { project_id: project._id, exclude_categories: ["BUG"], ...filters });
      if (!ok) return toast.error("Failed to fetch roadlog items");
      setItems(addPoints(data));
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch roadlog items");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (itemId, field, value) => {
    try {
      const { ok, data } = await api.put(`/roadlog_item/${itemId}`, { [field]: value });
      if (!ok) return toast.error("Failed to update");

      const index = items.findIndex((item) => item._id === itemId);
      const newItems = [...items];
      newItems[index] = { ...newItems[index], [field]: value };
      setItems(addPoints(newItems));
      toast.success(`updated successfully`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update");
    }
  };

  const addPoints = (arr1) => {
    const arr = [...arr1];
    for (let i = 0; i < arr.length; i++) {
      let chargePoints = 0;
      if (arr[i].charge === "XL") chargePoints += 1;
      if (arr[i].charge === "L") chargePoints += 2;
      if (arr[i].charge === "M") chargePoints += 3;
      if (arr[i].charge === "S") chargePoints += 4;
      if (arr[i].charge === "XS") chargePoints += 5;

      let businessPoints = 0;
      if (arr[i].business_contribution === "XL") businessPoints += 5;
      if (arr[i].business_contribution === "L") businessPoints += 4;
      if (arr[i].business_contribution === "M") businessPoints += 3;
      if (arr[i].business_contribution === "S") businessPoints += 2;
      if (arr[i].business_contribution === "XS") businessPoints += 1;

      let basePoints = chargePoints * businessPoints;
      let urgentPoints = arr[i].urgent ? 100 : 0;
      arr[i].points = basePoints + urgentPoints;

      let days_required = 0;
      if (arr[i].charge === "XL") days_required += 20;
      if (arr[i].charge === "L") days_required += 15;
      if (arr[i].charge === "M") days_required += 10;
      if (arr[i].charge === "S") days_required += 5;
      if (arr[i].charge === "XS") days_required += 1;
      arr[i].days_required = days_required;
    }
    return arr;
  };

  return (
    <div className="space-y-1">
      <TopMenu
        filters={filters}
        setFilters={setFilters}
        project={project}
        items={items}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onMultipleDelete={() => {
          setItems(items.filter((i) => !selectedItems.some((s) => s._id === i._id)));
          setSelectedItems([]);
        }}
        onTicketCreated={(newTicket) => {
          setItems([...items, newTicket]);
        }}
      />

      <div className="w-full rounded overflow-x-auto no-scrollbar h-[calc(100vh-130px)]">
        <table className="w-full">
          <thead className="text-left bg-white shadow-sm">
            <tr className="border-b">
              <th className="p-2">
                <div className="flex items-center justify-center gap-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems(items);
                      } else {
                        setSelectedItems([]);
                      }
                    }}
                  />
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold">Title</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "title" ? { key: "title", value: sortBy.value * -1 } : { key: "title", value: 1 });
                    }}>
                    {sortBy.key === "title" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "title" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>

              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold">Tags</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "roadlog_tags" ? { key: "roadlog_tags", value: sortBy.value * -1 } : { key: "roadlog_tags", value: 1 });
                    }}>
                    {sortBy.key === "roadlog_tags" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "roadlog_tags" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>

              <th className="p-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <h3 className="text-xs font-semibold">Charge</h3>
                  </div>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "charge" ? { key: "charge", value: sortBy.value * -1 } : { key: "charge", value: 1 });
                    }}>
                    {sortBy.key === "charge" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "charge" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <h3 className="text-xs font-semibold">Apport Business</h3>
                  </div>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "business_contribution" ? { key: "business_contribution", value: sortBy.value * -1 } : { key: "business_contribution", value: 1 });
                    }}>
                    {sortBy.key === "business_contribution" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "business_contribution" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold">Urgent</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "urgent" ? { key: "urgent", value: sortBy.value * -1 } : { key: "urgent", value: 1 });
                    }}>
                    {sortBy.key === "urgent" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "urgent" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold">Points</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "points" ? { key: "points", value: sortBy.value * -1 } : { key: "points", value: 1 });
                    }}>
                    {sortBy.key === "points" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "points" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold">Status</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "status" ? { key: "status", value: sortBy.value * -1 } : { key: "status", value: 1 });
                    }}>
                    {sortBy.key === "status" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "status" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="relative">
            {loading ? (
              <tr>
                <td colSpan={8}>
                  <div className="py-4 flex w-full justify-center bg-gray-50">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-4 text-center bg-gray-50">
                  No data found
                </td>
              </tr>
            ) : (
              <AnimatePresence>
                {items
                  .sort((a, b) => {
                    if (sortBy.key === "created_at") return sortBy.value * (new Date(b.created_at) - new Date(a.created_at));
                    if (sortBy.key === "points") return sortBy.value * (b.points - a.points);
                    if (sortBy.key === "roadlog_tags") {
                      const tagsA = a.roadlog_tags || [];
                      const tagsB = b.roadlog_tags || [];
                      // Sort by tag count first
                      if (tagsA.length !== tagsB.length) {
                        return sortBy.value * (tagsA.length - tagsB.length);
                      }
                      // If same count, sort alphabetically by first tag name
                      const tagNameA = tagsA.length > 0 ? tagsA[0].name.toLowerCase() : "";
                      const tagNameB = tagsB.length > 0 ? tagsB[0].name.toLowerCase() : "";
                      return sortBy.value * tagNameA.localeCompare(tagNameB);
                    }
                    return sortBy.value * (a[sortBy.key] || "").localeCompare(b[sortBy.key] || "");
                  })
                  .map((item, index) => {
                    const selected = selectedItems.some((i) => i._id === item._id);

                    return (
                      <motion.tr
                        key={item._id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className={`${index % 2 === 0 ? "bg-gray-100" : "bg-gray-50"} cursor-pointer`}>
                        <td
                          className="border px-2 py-2 cursor-pointer"
                          onClick={(e) => {
                            if (selected) {
                              setSelectedItems(selectedItems.filter((i) => i._id !== item._id));
                            } else {
                              setSelectedItems([...selectedItems, item]);
                            }
                          }}>
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={selected} onChange={() => {}} />
                        </td>
                        <td
                          onClick={() => {
                            query.set("item_modal_id", item._id);
                            navigate({ search: query.toString() });
                          }}
                          className="border px-2 py-2 text-sm hover:bg-gray-200">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">#{item.index}</span>
                              <span className="line-clamp-3">{item.title}</span>
                            </div>
                            {item.module_name && (
                              <div className="mt-1">
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{item.module_name}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="border px-2 py-2">
                          <TableTags item={item} project={project} onUpdate={handleSave} />
                        </td>
                        <EditableSelectCell itemId={item._id} field="charge" value={item.charge} options={["TO DEFINE", "XS", "S", "M", "L", "XL"]} onSave={handleSave} />
                        <EditableSelectCell
                          itemId={item._id}
                          field="business_contribution"
                          value={item.business_contribution}
                          options={["TO DEFINE", "XS", "S", "M", "L", "XL"]}
                          onSave={handleSave}
                        />
                        <EditableSelectCell itemId={item._id} field="urgent" value={item.urgent ? "yes" : "no"} options={["yes", "no"]} onSave={handleSave}>
                          <UrgentTag item={item} />
                        </EditableSelectCell>
                        <td className="border px-2 py-2 max-w-xs truncate text-xs">{item.points}</td>
                        <EditableSelectCell
                          itemId={item._id}
                          field="status"
                          value={item.status}
                          options={["TODO", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO", "DONE", "ARCHIVED"]}
                          onSave={handleSave}>
                          <StatusTag item={item} />
                        </EditableSelectCell>
                      </motion.tr>
                    );
                  })}
              </AnimatePresence>
            )}
          </tbody>
        </table>
      </div>
      {itemModalId && (
        <ItemModal
          project={project}
          onClose={() => {
            query.delete("item_modal_id");
            navigate({ search: query.toString() });
          }}
          onUpdate={(item) => setItems(items.map((i) => (i._id === item._id ? item : i)))}
          onDelete={(item) => setItems(items.filter((i) => i._id !== item._id))}
        />
      )}
    </div>
  );
};

const EditableSelectCell = ({ itemId, field, value, options, onSave, children }) => {
  const [editing, setEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const cellRef = useRef(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleSave = async (e) => {
    const newValue = e.target.value;
    setCurrentValue(newValue);
    setEditing(false);
    await onSave(itemId, field, field === "urgent" ? newValue === "yes" : newValue);
  };

  const handleClickOutside = (event) => {
    if (cellRef.current && !cellRef.current.contains(event.target)) {
      setEditing(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <td
      ref={cellRef}
      onClick={() => setEditing(true)}
      onBlur={() => setEditing(false)}
      className={`border pl-2 py-2 w-fit truncate text-xs hover:bg-gray-200 ${field === "status" ? "pr-2" : ""}`}>
      {editing ? (
        <select className="input p-1 text-xs" value={currentValue} onChange={handleSave}>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        children || currentValue
      )}
    </td>
  );
};

const StatusTag = ({ item }) => {
  if (!item?.status) return null;
  const colors = {
    TODO: "bg-blue-600 text-xs text-white border-gray-700",
    READY_TO_DEV: "bg-indigo-100 text-xs text-indigo-800",
    IN_PROGRESS: "bg-blue-100 text-xs text-blue-800",
    NEED_INFO: "bg-yellow-100 text-xs text-yellow-800",
    DONE: "bg-green-100 text-xs text-green-800",
    ARCHIVED: "bg-gray-100 text-xs text-gray-800",
  };
  return <div className={`px-2 py-1 rounded w-fit ${colors[item?.status]}`}>{item?.status}</div>;
};

const UrgentTag = ({ item }) => {
  const colors = {
    true: "bg-red-600 text-xs text-white border border-red-700",
    false: "bg-green-100 text-xs text-green-800 border border-green-200",
  };
  return <div className={`px-2 py-1 rounded w-fit ${colors[item?.urgent]}`}>{item?.urgent ? "Yes" : "No"}</div>;
};

const TableTags = ({ item, project, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tags, setTags] = useState(item.roadlog_tags || []);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const tagsId = `tags-select-${item._id}`;

  useEffect(() => {
    setTags(item.roadlog_tags || []);
  }, [item.roadlog_tags]);

  useEffect(() => {
    if (isOpen) {
      getTags();
    }
  }, [isOpen]);

  async function getTags() {
    setIsLoading(true);
    try {
      const { ok, data } = await api.post("/roadlog_tag/search", { project_id: project._id, limit: 1000, sort: "name" });
      if (!ok) throw new Error("Failed to fetch tags");
      setOptions(data);
    } catch (error) {
      console.error("Error fetching tags:", error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateNew(newOption) {
    setIsLoading(true);
    try {
      const { ok, data } = await api.post("/roadlog_tag", {
        name: newOption.label,
        project_id: project._id,
      });
      console.log("data", data);
      if (!ok) throw new Error("Failed to create tag");

      // Create a proper tag object with the server-generated ObjectId
      const newTag = { _id: data._id, name: data.name };
      const newTagsList = [...tags, newTag];
      setTags(newTagsList);
      setOptions([...options, newTag]);
      onUpdate(item._id, "roadlog_tags", newTagsList);

      // Close the dropdown after creating
      setIsOpen(false);
    } catch (error) {
      console.error("Error creating tag:", error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleSelectionChange(selectedOptions) {
    const newTagsList = selectedOptions.map((opt) => ({ _id: opt.value, name: opt.label }));
    setTags(newTagsList);
    onUpdate(item._id, "roadlog_tags", newTagsList);
  }

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const idRegex = new RegExp(tagsId);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !idRegex.test(event.target.id)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        id={tagsId}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="w-full text-left py-2 hover:bg-gray-100 rounded-md transition-colors text-xs">
        <div className="flex flex-wrap gap-1.5">
          {tags.length > 0 ? (
            <div>
              {tags.slice(0, 2).map((tag) => (
                <span
                  key={tag._id}
                  style={{
                    backgroundColor: tag.color ? `${tag.color}20` : "#EBF5FF",
                    color: tag.color || "#1E40AF",
                    borderColor: tag.color ? `${tag.color}40` : "#BFDBFE",
                  }}
                  className={`inline-flex items-center px-2 py-0.5 my-0.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap`}>
                  {tag.name}
                </span>
              ))}
              {tags.length > 2 && <span className="text-gray-500 text-xs italic">+{tags.length - 2}</span>}
            </div>
          ) : (
            <span className="text-gray-500 text-xs italic">Add tags...</span>
          )}
        </div>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] bg-white shadow-lg rounded-md border border-gray-200"
            style={{
              minWidth: "200px",
              width: "264px",
              top: buttonRef.current?.getBoundingClientRect().bottom + window.scrollY + 5,
              left: buttonRef.current?.getBoundingClientRect().left + window.scrollX,
            }}>
            <div className="p-2">
              <MultiSelect
                values={tags.map((tag) => ({ value: tag._id, label: tag.name }))}
                id={`select-tags-${item._id}`}
                options={options.map((tag) => ({ value: tag._id, label: tag.name }))}
                onSelectedChange={handleSelectionChange}
                placeholder="Search or create tags"
                buttonClassName="px-3 py-2"
                onCreateNew={handleCreateNew}
                isLoading={isLoading}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
